import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useHospitalStore } from '@/stores/hospitalStore'
import { useDoctorStore } from '@/stores/doctorStore'
import { usePatientStore } from '@/stores/patientStore'

export default function HospitalList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedHospitals, setSelectedHospitals] = useState<string[]>([])
  const [filterType, setFilterType] = useState<string>('all')
  
  const { hospitals, deleteHospital } = useHospitalStore()
  const { doctors } = useDoctorStore()
  const { patients } = usePatientStore()

  // 病院ごとの医師数と患者数を計算
  const hospitalsWithCounts = hospitals.map(hospital => {
    const doctorCount = doctors.filter(d => d.hospitalId === hospital.id).length
    const patientCount = patients.filter(p => p.primaryHospitalId === hospital.id || p.transplantHospitalId === hospital.id).length
    return {
      ...hospital,
      doctorCount,
      patientCount,
    }
  })

  const filteredHospitals = hospitalsWithCounts.filter((hospital) => {
    const matchesSearch =
      hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hospital.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hospital.representative.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType =
      filterType === 'all' ||
      hospital.type === filterType
    return matchesSearch && matchesType
  })

  const toggleSelectAll = () => {
    if (selectedHospitals.length === filteredHospitals.length) {
      setSelectedHospitals([])
    } else {
      setSelectedHospitals(filteredHospitals.map((h) => h.id))
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedHospitals((prev) =>
      prev.includes(id) ? prev.filter((h) => h !== id) : [...prev, id]
    )
  }

  const handleDeleteSelected = () => {
    if (confirm(`${selectedHospitals.length}件の病院を削除しますか？`)) {
      selectedHospitals.forEach(id => deleteHospital(id))
      setSelectedHospitals([])
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'クリニック': return 'クリニック'
      case '研究機関': return '研究機関'
      case '事務局': return '事務局'
      default: return type
    }
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">病院マスター</h1>
          <div className="aip-main__header-buttons">
            <Link to="/master/hospitals/create" className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">add</span>
              新規登録
            </Link>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        <div className="list-container">
          {/* ツールバー */}
          <div className="aip-table__header">
            <div className="aip-table__header-area">
              <div className="aip-search-bar">
                <span className="material-symbols-outlined aip-search-bar__icon">search</span>
                <input
                  className="aip-search-bar__input"
                  type="text"
                  placeholder="病院名、ID、代表者名で検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                className="aip-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">すべての種別</option>
                <option value="クリニック">クリニック</option>
                <option value="研究機関">研究機関</option>
                <option value="事務局">事務局</option>
              </select>
            </div>
            <div className="aip-table__header-area">
              <span className="result-count">{filteredHospitals.length}件</span>
            </div>
          </div>

          {/* 選択時のアクションバー */}
          {selectedHospitals.length > 0 && (
            <div className="aip-table__selection-bar">
              <span>{selectedHospitals.length}件選択中</span>
              <button className="aip-button aip-button--outline" onClick={handleDeleteSelected}>
                <span className="material-symbols-outlined">delete</span>
                削除
              </button>
            </div>
          )}

          {/* テーブル */}
          <div className="aip-table-wrapper">
            <table className="aip-table">
              <thead>
                <tr>
                  <th className="aip-table__checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedHospitals.length === filteredHospitals.length && filteredHospitals.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>病院ID</th>
                  <th>病院名</th>
                  <th>種別</th>
                  <th>代表者</th>
                  <th>電話番号</th>
                  <th>医師数</th>
                  <th>患者数</th>
                  <th>状態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredHospitals.map((hospital) => (
                  <tr key={hospital.id}>
                    <td className="aip-table__checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedHospitals.includes(hospital.id)}
                        onChange={() => toggleSelect(hospital.id)}
                      />
                    </td>
                    <td>
                      <Link to={`/master/hospitals/${hospital.id}`} className="aip-table__link">
                        {hospital.id}
                      </Link>
                    </td>
                    <td>
                      <div className="hospital-name-cell">
                        <span className="hospital-name">{hospital.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`aip-badge aip-badge--${hospital.type === 'クリニック' ? 'info' : hospital.type === '研究機関' ? 'success' : 'neutral'}`}>
                        {getTypeLabel(hospital.type)}
                      </span>
                    </td>
                    <td>{hospital.representative}</td>
                    <td>{hospital.tel}</td>
                    <td className="text-center">{hospital.doctorCount}</td>
                    <td className="text-center">{hospital.patientCount}</td>
                    <td>
                      <span className={`aip-badge aip-badge--${hospital.status === 'active' ? 'success' : 'neutral'}`}>
                        {hospital.status === 'active' ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>
                      <div className="aip-table__actions">
                        <Link to={`/master/hospitals/${hospital.id}`} className="aip-button aip-button--icon aip-button--sm" title="詳細">
                          <span className="material-symbols-outlined">visibility</span>
                        </Link>
                        <Link to={`/master/hospitals/${hospital.id}/edit`} className="aip-button aip-button--icon aip-button--sm" title="編集">
                          <span className="material-symbols-outlined">edit</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          <div className="aip-table__pagination">
            <span className="aip-table__pagination-info">
              1 - {filteredHospitals.length} （{hospitals.length}件中）
            </span>
            <div className="aip-table__pagination-controls">
              <button className="aip-button aip-button--icon" disabled>
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button className="aip-button aip-button--icon">
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .list-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .aip-table__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-md);
          border-bottom: 1px solid var(--border-color);
        }

        .aip-table__header-area {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          flex-wrap: wrap;
        }

        .aip-select {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: var(--background-primary);
        }

        .result-count {
          font-size: 0.875rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .aip-table__selection-bar {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-blue50);
          border-bottom: 1px solid var(--color-blue200);
        }

        .aip-table-wrapper {
          overflow-x: auto;
        }

        .aip-table {
          width: 100%;
          border-collapse: collapse;
        }

        .aip-table th,
        .aip-table td {
          padding: var(--spacing-sm) var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
          white-space: nowrap;
          font-size: 0.8125rem;
        }

        .aip-table th {
          background-color: var(--color-solid-gray25);
          font-weight: 500;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-table tbody tr:hover {
          background-color: var(--color-solid-gray25);
        }

        .aip-table__checkbox-cell {
          width: 40px;
          text-align: center !important;
        }

        .aip-table__link {
          color: var(--color-blue600);
          font-weight: 500;
        }

        .aip-table__link:hover {
          text-decoration: underline;
        }

        .hospital-name-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .hospital-name {
          font-weight: 500;
        }

        .text-center {
          text-align: center;
        }

        .aip-badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 0.6875rem;
          font-weight: 500;
          border-radius: var(--border-radius-sm);
        }

        .aip-badge--primary {
          background-color: var(--color-blue100);
          color: var(--color-blue700);
        }

        .aip-badge--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .aip-badge--info {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        .aip-badge--warning {
          background-color: var(--color-focus-light);
          color: #8b6914;
        }

        .aip-badge--neutral {
          background-color: var(--color-solid-gray100);
          color: var(--color-solid-gray700);
        }

        .aip-table__actions {
          display: flex;
          gap: var(--spacing-xs);
        }

        .aip-table__pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-md);
          border-top: 1px solid var(--border-color);
        }

        .aip-table__pagination-info {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .aip-table__pagination-controls {
          display: flex;
          gap: var(--spacing-xs);
        }

        .aip-button--sm {
          padding: 4px;
        }

        .aip-button--sm .material-symbols-outlined {
          font-size: 20px;
        }
      `}</style>
    </>
  )
}
